/**
 * Sources worth turning into a skill, for the landing carousel.
 *
 * Every entry here was resolved against the real API before being written down
 * — YouTube ids and durations via yt-dlp, repositories via the GitHub API — so
 * no thumbnail 404s and no title is a paraphrase of the real one. If you add
 * one, verify it the same way rather than typing an id from memory: a broken
 * thumbnail on the homepage is worse than a shorter list.
 *
 * Thumbnails are stored under public/starter/ rather than hotlinked; see
 * thumbnailUrl below for why. Refresh them with scripts/fetch_starter_thumbs.py.
 *
 * `durationSeconds` and `stars` are for the corner badge only. Stars are a
 * snapshot and are shown rounded to the nearest thousand, which is the
 * precision anyone reads them at anyway.
 */

export const YOUTUBE = [
  {
    kind: 'youtube',
    id: '-QFHIoCo-Ko',
    title: 'Full Walkthrough: Workflow for AI Coding',
    author: 'Matt Pocock',
    durationSeconds: 5790,
  },
  {
    kind: 'youtube',
    id: '8rABwKRsec4',
    title: 'The New Code',
    author: 'Sean Grove, OpenAI',
    durationSeconds: 1296,
  },
  {
    kind: 'youtube',
    id: 'CEvIs9y1uog',
    title: "Don't Build Agents, Build Skills Instead",
    author: 'Barry Zhang & Mahesh Murag, Anthropic',
    durationSeconds: 982,
  },
  {
    kind: 'youtube',
    id: 'D7_ipDqhtwk',
    title: 'How We Build Effective Agents',
    author: 'Barry Zhang, Anthropic',
    durationSeconds: 909,
  },
  {
    kind: 'youtube',
    id: 'WkBPX-oDMnA',
    title: 'Understanding is the new bottleneck',
    author: 'Geoffrey Litt, Notion',
    durationSeconds: 1173,
  },
  {
    kind: 'youtube',
    id: 'UNzCG3lw6O0',
    title: 'Building Great Agent Skills: The Missing Manual',
    author: 'AI Engineer',
    durationSeconds: 1243,
  },
  {
    kind: 'youtube',
    id: 'M05vON8i0aI',
    title: 'AI-Native Organisations Run on Skills',
    author: 'Imad Touil, QuantumBlack',
    durationSeconds: 1231,
  },
  {
    kind: 'youtube',
    id: 'KHudyx5wW3U',
    title: 'Agent Frameworks Considered Harmful',
    author: 'Rémi Louf, .txt',
    durationSeconds: 1229,
  },
  {
    kind: 'youtube',
    id: 'sM1iYgz93HI',
    title: 'The Half Life of Agent Infrastructure',
    author: 'Ben Kus, Box',
    durationSeconds: 1166,
  },

  // Nate B Jones (AI News & Strategy Daily). His channel is mostly daily
  // commentary, which makes a poor skill — a skill needs a repeatable method,
  // not a take on this week's news. These are the ones that carry a procedure:
  // a taxonomy, a tutorial, a setup, a technique you could follow tomorrow.
  {
    kind: 'youtube',
    id: '9PUaEj0pMYE',
    title: 'Claude Skills vs MCP vs Prompts: The Difference Everyone Gets Wrong',
    author: 'Nate B Jones',
    durationSeconds: 1066,
  },
  {
    kind: 'youtube',
    id: 'xqGCbEDbny8',
    title: 'Codex Tutorial: Build Your First AI Agent Delegation Loop',
    author: 'Nate B Jones',
    durationSeconds: 1177,
  },
  {
    kind: 'youtube',
    id: 'BOXK2XFLA-E',
    title: "How To Build AI Agents: Don't Build Another One Until You Watch This",
    author: 'Nate B Jones',
    durationSeconds: 1105,
  },
  {
    kind: 'youtube',
    id: 'Y8vAQ1FgNbM',
    title: 'Paste This Into Claude, Never Hit a Token Limit Again',
    author: 'Nate B Jones',
    durationSeconds: 1217,
  },
  {
    kind: 'youtube',
    id: 'HgAQOkG_v8c',
    title: 'Claude Code Second Brain: I Built My Own AI Memory',
    author: 'Nate B Jones',
    durationSeconds: 977,
  },
  {
    kind: 'youtube',
    id: 'A4zMyjkL0Dc',
    title: 'AI Prompt Engineering: I Stopped Prompting One Task At A Time',
    author: 'Nate B Jones',
    durationSeconds: 939,
  },
  {
    kind: 'youtube',
    id: 'joRXo6x7Pgk',
    title: 'The Five Kinds of Software You Can Make',
    author: 'Nate B Jones',
    durationSeconds: 2099,
  },
];

export const REPOS = [
  {
    kind: 'repo',
    id: 'mrdoob/three.js',
    title: 'three.js',
    author: 'A JavaScript 3D library',
    stars: 115338,
  },
  {
    kind: 'repo',
    id: 'blender/blender',
    title: 'Blender',
    author: 'The 3D creation suite',
    stars: 20204,
  },
  {
    kind: 'repo',
    id: 'karpathy/nanoGPT',
    title: 'nanoGPT',
    author: 'Training and finetuning GPTs, minimally',
    stars: 62941,
  },
  {
    kind: 'repo',
    id: 'karpathy/nn-zero-to-hero',
    title: 'Neural Networks: Zero to Hero',
    author: 'Andrej Karpathy',
    stars: 24319,
  },
  {
    kind: 'repo',
    id: 'obsidianmd/obsidian-releases',
    title: 'Obsidian',
    author: 'Plugins, themes and releases',
    stars: 21424,
  },
  {
    kind: 'repo',
    id: 'modelcontextprotocol/servers',
    title: 'MCP Servers',
    author: 'Model Context Protocol',
    stars: 90185,
  },
  {
    kind: 'repo',
    id: 'fastapi/fastapi',
    title: 'FastAPI',
    author: 'The Python web framework',
    stars: 102212,
  },
];

export const STARTER_SOURCES = [...YOUTUBE, ...REPOS];

export function sourceUrl(item) {
  return item.kind === 'youtube'
    ? `https://www.youtube.com/watch?v=${item.id}`
    : `https://github.com/${item.id}`;
}

export function thumbnailUrl(item) {
  // Served from our own origin. Hotlinking these looked free and is not:
  // opengraph.githubassets.com answers 429 under any real traffic, so the
  // repository cards would go blank exactly when the page got popular — and
  // hotlinking i.ytimg.com hands every visitor's IP to Google before they have
  // clicked anything. scripts/fetch_starter_thumbs.py refreshes them.
  return item.kind === 'youtube'
    ? `/starter/yt-${item.id}.jpg`
    : `/starter/gh-${item.id.replace('/', '--')}.jpg`;
}

export function badgeText(item) {
  if (item.kind === 'youtube') {
    const s = item.durationSeconds;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = String(s % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
  }
  const k = item.stars / 1000;
  return `★ ${k >= 100 ? Math.round(k) : k.toFixed(1)}k`;
}

/** Fisher-Yates. A fresh order every load, so the same few never lead. */
export function shuffled(items) {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
